import React, { useEffect, useRef, useState } from "react";

interface HorizontalBarChartProps {
  labels: string[];
  values: number[];
  maxX?: number;
  mirrored?: boolean;
  title?: string;
}

// Minimum horizontal room (in px) we want between the centers of two
// *visible* X-axis tick labels. Each label element is 2em wide (~24px at
// text-xs 12px), so 32px keeps adjacent labels from touching. When ticks are
// closer than this, we thin the labels (show every Nth) instead of dropping
// them entirely — that way the endpoints (0 and max) are always visible.
const MIN_PX_BETWEEN_SHOWN_LABELS = 32;

const HorizontalBarChart: React.FC<HorizontalBarChartProps> = ({ labels, values, maxX, mirrored, title }) => {
  // Determine bucket size
  const rawMax = Math.max(...values, 0);
  let bucketSize = 1000;
  if (rawMax < 1000) {
    bucketSize = 100;
  } else if (rawMax > 20000) {
    bucketSize = 5000;
  } else if (rawMax > 10000) {
    bucketSize = 2000;
  }

  // Compute the upper bound (next bucket above max)
  const computedMax = maxX || Math.ceil(rawMax / bucketSize) * bucketSize;

  // Generate bucket markers
  const buckets = [];
  for (let v = 0; v <= computedMax; v += bucketSize) {
    buckets.push(v);
  }

  // Track the pixel width of the graph area so we can drop the X-axis labels
  // when they'd become too cramped to read. This depends on both the panel
  // width and the number of bucket ticks.
  const graphAreaRef = useRef<HTMLDivElement | null>(null);
  const [graphAreaWidth, setGraphAreaWidth] = useState(0);

  useEffect(() => {
    const el = graphAreaRef.current;
    if (!el) return;
    const update = () => setGraphAreaWidth(el.getBoundingClientRect().width);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const showBucketLabels = buckets.length > 0;
  // Choose a step so shown labels stay >= MIN_PX_BETWEEN_SHOWN_LABELS apart.
  // Before the first measurement (graphAreaWidth === 0), default to step=1
  // so labels appear on initial render.
  let labelStep = 1;
  if (graphAreaWidth > 0 && buckets.length > 2) {
    const spacingPx = graphAreaWidth / (buckets.length - 1);
    labelStep = Math.max(1, Math.ceil(MIN_PX_BETWEEN_SHOWN_LABELS / spacingPx));
  }

  return (
    <div className="w-full h-full flex flex-col py-4" style={{ marginRight: '3rem' }}>
      {title && <div className="text-lg font-bold mb-6 text-center" style={{ color: 'var(--dark-color-text)' }}>{title}</div>}
      <div className="flex-1 min-h-0 flex flex-col">
        {/* Bucket lines and bars overlay, both offset by ml-40 (10rem) */}
        <div className="flex flex-1 min-h-0">
          {/* Left label margin */}
          <div className="w-40" />
          {/* Graph area */}
          <div className="relative flex-1" ref={graphAreaRef}>
            {/* Vertical bucket lines */}
            <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 1 }}>
              <div className="relative w-full h-full">
                {buckets.map((v) => {
                  const leftPercent = (v / computedMax) * 100;
                  return (
                    <div
                      key={v}
                      style={{
                        position: 'absolute',
                        left: `calc(${leftPercent}% - 1px)` ,
                        top: 0,
                        bottom: 0,
                        width: '2px',
                        background: 'var(--dark-color-border-color)',
                        opacity: 0.5,
                      }}
                    />
                  );
                })}
              </div>
            </div>
            {/* Bars and labels — each row uses flex-1 so they distribute the
                 graph area's vertical space, letting the spacing between bars
                 grow as the panel is dragged taller. */}
            <div className="flex flex-col relative z-10 h-full">
              {labels.map((label, idx) => {
                const value = values[idx] || 0;
                const percent = Math.min((value / computedMax) * 100, 100);
                return (
                  <div key={label} className="flex flex-1 items-center w-full" style={{ minHeight: '2rem' }}>
                    {/* Label is outside graph area */}
                    <span className="absolute -left-40 w-40 text-xs truncate mr-4 text-right pr-4" style={{ color: 'var(--dark-color-text)', fontSize: '16px' }}>{label}</span>
                    <div className="flex-1 h-5 rounded relative bg-transparent ml-0" style={{ overflow: 'visible' }}>
                      <div
                        className="h-full rounded transition-all duration-300"
                        style={{ width: `${percent}%`, background: 'var(--dark-color-accent-blue)' }}
                      />
                      {/* Value at end of bar */}
                      <span
                        className="absolute"
                        style={{
                          left: percent === 100 ? `calc(${percent}% + 0.5rem)` : `calc(${percent}% + 0.5rem)`,
                          top: 0,
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          fontSize: '16px',
                          color: 'var(--dark-color-text)',
                          whiteSpace: 'nowrap',
                          zIndex: 20,
                          overflow: 'visible',
                          paddingRight: percent === 100 ? '2.5rem' : undefined,
                        }}
                      >
                        {value >= 1000 ? `${parseFloat((value / 1000).toFixed(1))}k` : parseFloat(value.toFixed(1))}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
      {/* X axis — bucket labels are thinned automatically (every Nth shown)
          when the graph area is too narrow to fit them all without overlap.
          The first (0) and last (max) tick are always shown. */}
      <div className="relative mt-4 flex">
        <div className="w-40" />
        <div className="relative flex-1" style={{ height: '1.5em' }}>
          {showBucketLabels && buckets.map((v, idx) => {
            const isLast = idx === buckets.length - 1;
            // Always show first + last; otherwise every Nth. If the last tick
            // would land too close to a stepped tick, we still prefer showing
            // the endpoint since it anchors the axis scale.
            if (idx !== 0 && !isLast && idx % labelStep !== 0) return null;
            const leftPercent = (v / computedMax) * 100;
            // Abbreviate label: show hundreds if < 1000, else k
            let label;
            if (bucketSize < 1000) {
              label = v === 0 ? '0' : `${v / 100}00`;
            } else {
              label = v === 0 ? '0k' : `${v / 1000}k`;
            }
            return (
              <div
                key={v}
                className="text-xs absolute"
                style={{
                  color: 'var(--dark-color-text)',
                  left: `calc(${leftPercent}% - 1em)`,
                  width: '2em',
                  textAlign: 'center',
                }}
              >
                {label}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default HorizontalBarChart;
